import { useEffect, useRef, useState, useId } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { startOfMonth, endOfMonth, parseISO, min as dateMin, max as dateMax, differenceInCalendarDays, startOfYear } from 'date-fns';
import { dateToX, ROW_HEIGHT, COLORS, formatRent } from './calendarConstants';

// ─────────────────────────────────────────────────────────────────────────
// Global one-open-at-a-time coordinator
//
// Every calendar popover (per-month tooltip, per-unit YTD popover)
// broadcasts a `calendar:popover-open` event with its own id when it opens.
// All other mounted popovers listen for it and close themselves whenever the
// event's id differs from their own. This means clicking any month cell in
// any row, or any unit box, always dismisses whatever else was open.
// ─────────────────────────────────────────────────────────────────────────
const CAL_POPOVER_EVENT = 'flexliving:calendar-popover-open';

function broadcastOpen(id) {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(CAL_POPOVER_EVENT, { detail: { id } }));
  }
}

function useCloseOnOtherOpen(myId, onOtherOpen) {
  const cb = useRef(onOtherOpen);
  cb.current = onOtherOpen;
  useEffect(() => {
    const handler = (e) => {
      if (e?.detail?.id !== myId) cb.current();
    };
    window.addEventListener(CAL_POPOVER_EVENT, handler);
    return () => window.removeEventListener(CAL_POPOVER_EVENT, handler);
  }, [myId]);
}

// Compute occupied days count within [rangeStart, rangeEnd] across all bookings.
export function occupiedDays(rangeStart, rangeEnd, bookings) {
  let days = 0;
  for (const b of (bookings || [])) {
    const bStart = parseISO(b.start_date);
    const bEnd = parseISO(b.end_date);
    const os = dateMax([bStart, rangeStart]);
    const oe = dateMin([bEnd, rangeEnd]);
    const d = differenceInCalendarDays(oe, os) + 1;
    if (d > 0) days += d;
  }
  return days;
}

// Compute the revenue attributable to a single unit within a date window.
// If the unit has a backend-provided `monthly_income` array (which uses the
// same rent-roll accounting as /api/income — whole-month billing with
// partial_first/last overrides), we sum the months that overlap the window.
// Otherwise we fall back to day-proration for legacy safety.
export function revenueForRange(rangeStart, rangeEnd, bookings, monthlyIncome = null) {
  if (monthlyIncome && monthlyIncome.length) {
    let total = 0;
    for (const entry of monthlyIncome) {
      const mStart = new Date(entry.year, entry.month - 1, 1);
      const mEnd = new Date(entry.year, entry.month, 0);   // last day of month
      // Include the month if it overlaps the range at all — matches the
      // rent roll's whole-month semantics.
      if (mEnd < rangeStart || mStart > rangeEnd) continue;
      total += entry.income || 0;
    }
    return Math.round(total);
  }
  // Fallback: day-proration (only used if backend hasn't returned monthly_income).
  let total = 0;
  const windowDays = differenceInCalendarDays(rangeEnd, rangeStart) + 1;
  if (windowDays <= 0) return 0;
  for (const b of (bookings || [])) {
    const bStart = parseISO(b.start_date);
    const bEnd = parseISO(b.end_date);
    const os = dateMax([bStart, rangeStart]);
    const oe = dateMin([bEnd, rangeEnd]);
    const days = differenceInCalendarDays(oe, os) + 1;
    if (days <= 0) continue;
    const rent = Number(b.rent_amount) || 0;
    if (!rent) continue;
    if (b.is_airbnb_vrbo) {
      const totalDays = differenceInCalendarDays(bEnd, bStart) + 1;
      if (totalDays > 0) total += rent * (days / totalDays);
    } else {
      total += (rent / 30) * days;
    }
  }
  return Math.round(total);
}

// Per-month revenue lookup. Prefers backend monthly_income; falls back to
// day-based proration.
function monthRevenue(month, bookings, monthlyIncome = null) {
  if (monthlyIncome && monthlyIncome.length) {
    const y = month.getFullYear();
    const m = month.getMonth() + 1;
    const entry = monthlyIncome.find((e) => e.year === y && e.month === m);
    return Math.round(entry?.income || 0);
  }
  const ms = startOfMonth(month);
  const me = endOfMonth(month);
  let total = 0;
  for (const b of (bookings || [])) {
    const bStart = parseISO(b.start_date);
    const bEnd = parseISO(b.end_date);
    const overlapStart = dateMax([bStart, ms]);
    const overlapEnd = dateMin([bEnd, me]);
    const days = differenceInCalendarDays(overlapEnd, overlapStart) + 1;
    if (days <= 0) continue;
    const rent = Number(b.rent_amount) || 0;
    if (!rent) continue;
    if (b.is_airbnb_vrbo) {
      const totalDays = differenceInCalendarDays(bEnd, bStart) + 1;
      if (totalDays > 0) total += rent * (days / totalDays);
    } else {
      const monthDays = differenceInCalendarDays(me, ms) + 1;
      total += rent * (days / monthDays);
    }
  }
  return Math.round(total);
}

const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

/**
 * Overlay one click-target per month. Sits BEHIND booking bars (zIndex 0)
 * so its onClick fires only when the user actually clicks empty vacant
 * area — booking bars intercept clicks first with their own handlers.
 */
export function MonthRevenueOverlay({ months, bookings, monthlyIncome, rangeStart, unitName }) {
  const [openIdx, setOpenIdx] = useState(null);
  const myId = useId();

  // Close if another calendar popover (anywhere on the page) opens.
  useCloseOnOtherOpen(myId, () => setOpenIdx(null));

  // Broadcast our open state via effect (never inside a setState updater —
  // that would violate React's "setState in render" rule since the sibling
  // popovers' event listeners call their own setState synchronously).
  useEffect(() => {
    if (openIdx != null) broadcastOpen(myId);
  }, [openIdx, myId]);

  // Any click OFF this overlay closes it. We tag this overlay's own month
  // cells with `data-month-cell-owner={myId}` so clicking within THIS
  // overlay's own cells doesn't immediately re-close before the toggle
  // handler runs.
  useEffect(() => {
    if (openIdx == null) return;
    const close = (e) => {
      const cell = e.target?.closest?.('[data-month-cell-owner]');
      if (cell && cell.getAttribute('data-month-cell-owner') === myId) return;
      if (e.target?.closest?.(`[data-popover-owner="${myId}"]`)) return;
      setOpenIdx(null);
    };
    document.addEventListener('click', close, true);
    return () => document.removeEventListener('click', close, true);
  }, [openIdx, myId]);

  if (!months || months.length === 0) return null;
  return (
    <>
      {months.map((month, i) => {
        const nextMonth = months[i + 1];
        const leftX = dateToX(month, rangeStart);
        const nextX = nextMonth ? dateToX(nextMonth, rangeStart) : null;
        const width = nextX != null ? nextX - leftX : null;
        return (
          <div
            key={`mo-${i}`}
            className="absolute top-0 h-full cursor-pointer"
            style={{ left: leftX, width: width || 'auto', right: width == null ? 0 : undefined, zIndex: 0 }}
            onClick={(e) => {
              e.stopPropagation();
              setOpenIdx((idx) => (idx === i ? null : i));
            }}
            data-testid={`calendar-month-cell-${i}`}
            data-month-cell-owner={myId}
          />
        );
      })}
      {openIdx != null && (() => {
        const month = months[openIdx];
        const nextMonth = months[openIdx + 1];
        const leftX = dateToX(month, rangeStart);
        const nextX = nextMonth ? dateToX(nextMonth, rangeStart) : leftX + 120;
        const centerX = (leftX + nextX) / 2;
        const ms = startOfMonth(month);
        const me = endOfMonth(month);
        const revenue = monthRevenue(month, bookings, monthlyIncome);
        const occDays = occupiedDays(ms, me, bookings);
        const monthDays = differenceInCalendarDays(me, ms) + 1;
        const occPct = monthDays > 0 ? Math.round((occDays / monthDays) * 100) : 0;
        return (
          <div
            className="absolute -top-2 -translate-x-1/2 -translate-y-full z-50 bg-card border border-border rounded-lg shadow-lg px-3 py-2 text-xs whitespace-nowrap"
            style={{ left: centerX, backgroundColor: COLORS.card || '#fff' }}
            data-testid="calendar-month-revenue-tooltip"
            data-popover-owner={myId}
          >
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
              {MONTH_NAMES[month.getMonth()]} {month.getFullYear()} · {unitName || 'Unit'}
            </p>
            <p className="text-sm font-semibold text-foreground tabular-nums mt-0.5">
              Revenue: {formatRent(revenue)}
            </p>
            <p className="text-[11px] text-muted-foreground tabular-nums mt-0.5">
              Occupancy: <span className="font-medium text-foreground">{occPct}%</span> ({occDays}/{monthDays} days)
            </p>
          </div>
        );
      })()}
    </>
  );
}

/**
 * Left-column "unit box" click → year-to-date summary popover (revenue,
 * average / month, occupancy). Skips days before `openDate` so brand-new
 * units are not penalised on occupancy for months they weren't listed.
 *
 * The trigger fills its parent (`w-full h-full`) so the entire unit box
 * on the left column is clickable, not just the badge.
 */
export function UnitYearSummaryButton({ children, unit, className = '', testId = 'unit-year-summary' }) {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [coords, setCoords] = useState(null);
  const triggerRef = useRef(null);
  const myId = useId();

  useCloseOnOtherOpen(myId, () => setOpen(false));

  // Broadcast our "just opened" state via effect so React never sees a
  // setState-in-render (siblings' listeners call their own setState).
  useEffect(() => {
    if (open) broadcastOpen(myId);
  }, [open, myId]);

  // Compute viewport coords for the popover on open. Using position:fixed
  // escapes the calendar's left-column `overflow-hidden` clipping (which
  // would otherwise hide the popover completely on mobile / narrow columns).
  useEffect(() => {
    if (!open || !triggerRef.current) return;
    const compute = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const POP_W = 260;      // popover approx width (min-w-[240] + padding)
      const POP_H = 160;
      const GAP = 4;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      // Prefer to the right of the trigger.
      let left = rect.right + GAP;
      let top = rect.top;
      if (left + POP_W > vw - 8) {
        // Flip to left of trigger.
        left = rect.left - POP_W - GAP;
      }
      if (left < 8) {
        // Still off-screen — anchor below the trigger, clamped inside viewport.
        left = Math.max(8, Math.min(vw - POP_W - 8, rect.left));
        top = rect.bottom + GAP;
      }
      // Clamp vertically so the popover doesn't fall off the bottom.
      if (top + POP_H > vh - 8) top = Math.max(8, vh - POP_H - 8);
      setCoords({ top, left });
    };
    compute();
    window.addEventListener('resize', compute);
    window.addEventListener('scroll', compute, true);
    return () => {
      window.removeEventListener('resize', compute);
      window.removeEventListener('scroll', compute, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const close = (e) => {
      if (e.target?.closest?.(`[data-popover-owner="${myId}"]`)) return;
      if (e.target?.closest?.(`[data-testid="${testId}-trigger"]`)) return;
      setOpen(false);
    };
    document.addEventListener('click', close, true);
    return () => document.removeEventListener('click', close, true);
  }, [open, testId, myId]);

  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;   // 1..12
  const yStart = startOfYear(now);
  // "End of previous month" — day 0 of currentMonth (1-based) = last day of currentMonth-1.
  const endOfPrevMonth = new Date(currentYear, currentMonth - 1, 0);
  const activationStr = unit?.active_since || unit?.availability_start_date || unit?.open_date || unit?.created_at || unit?.listed_at;
  const activationDate = activationStr ? parseISO(activationStr) : null;

  // Revenue = sum of ALL income for months Jan → end of previous month
  // (no available_since filter). Matches the rent-roll accounting.
  //
  // Avg / month = sum of ELIGIBLE-full-months income ÷ eligible full months.
  // A month is "eligible" only if the unit was available on/before that
  // month's first day (i.e., unit was available for the entire month).
  const monthlyIncome = unit?.monthly_income || [];
  let rev = 0;
  let eligibleIncome = 0;
  let eligibleMonthCount = 0;
  for (const entry of monthlyIncome) {
    if (entry.year !== currentYear) continue;
    if (entry.month < 1 || entry.month >= currentMonth) continue;   // exclude current + future
    rev += entry.income || 0;
    const mStart = new Date(entry.year, entry.month - 1, 1);
    if (!activationDate || activationDate <= mStart) {
      eligibleIncome += entry.income || 0;
      eligibleMonthCount += 1;
    }
  }
  rev = Math.round(rev);
  const hasAvg = eligibleMonthCount > 0;
  const avgPerMonth = hasAvg ? Math.round(eligibleIncome / eligibleMonthCount) : null;

  // Occupancy: day-level, from max(Jan 1, available_since) → end of prev month.
  const occStart = activationDate && activationDate > yStart ? activationDate : yStart;
  let days = 0;
  let occ = 0;
  let occPct = null;
  if (occStart <= endOfPrevMonth) {
    days = differenceInCalendarDays(endOfPrevMonth, occStart) + 1;
    occ = occupiedDays(occStart, endOfPrevMonth, unit?.bookings || []);
    occPct = days > 0 ? Math.round((occ / days) * 100) : 0;
  }
  const effectiveStart = occStart;   // kept for the "Since available" footer

  return (
    <div className="relative w-full h-full">
      <button
        ref={triggerRef}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className={`w-full h-full text-left cursor-pointer hover:bg-muted/60 transition-colors ${className}`}
        data-testid={`${testId}-trigger`}
      >
        {children}
      </button>
      {open && coords && (
        <div
          className="fixed z-[60] bg-card border border-border rounded-lg shadow-lg px-3 py-2 text-xs whitespace-nowrap min-w-[240px]"
          style={{ backgroundColor: COLORS.card || '#fff', top: coords.top, left: coords.left }}
          data-testid={`${testId}-popover`}
          data-popover-owner={myId}
        >
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
            {currentYear} to date · {unit?.unit_number || 'Unit'}
            {unit?.unit_size ? <span className="normal-case text-muted-foreground/80"> · {unit.unit_size}</span> : null}
          </p>
          <p className="text-sm font-semibold text-foreground tabular-nums mt-0.5" data-testid={`${testId}-revenue`}>
            Revenue: {formatRent(rev)}
          </p>
          <p className="text-[11px] text-muted-foreground tabular-nums mt-0.5" data-testid={`${testId}-avg`}>
            Avg / month: <span className="font-medium text-foreground">{hasAvg ? formatRent(avgPerMonth) : '—'}</span>
            {hasAvg && eligibleMonthCount < (currentMonth - 1) && (
              <span className="text-muted-foreground/80"> ({eligibleMonthCount}-mo)</span>
            )}
          </p>
          <p className="text-[11px] text-muted-foreground tabular-nums mt-0.5" data-testid={`${testId}-occupancy`}>
            Occupancy: <span className="font-medium text-foreground">{occPct != null ? `${occPct}%` : '—'}</span>
            {occPct != null && <span> ({occ}/{days} days)</span>}
          </p>
          {activationDate && activationDate > yStart && (
            <p className="text-[10px] text-muted-foreground italic mt-1">
              Since available {activationDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}.
            </p>
          )}
          <div className="mt-2 pt-2 border-t border-border/60">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}
              className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors"
              data-testid={`${testId}-expand`}
            >
              {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
              {expanded ? 'Hide notes' : 'More'}
            </button>
            {expanded && (
              <div
                className="mt-2 rounded-md bg-muted/40 border border-dashed border-border/60 px-2 py-2 text-[11px] text-muted-foreground whitespace-normal"
                data-testid={`${testId}-notes`}
              >
                Notes area — coming soon.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
